import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Fixture } from '@/types/fixtures';
import { VesselBadge } from './VesselBadge';
import { FixtureStatus } from './FixtureStatus';
import { formatCargoSize, formatDate, formatLayCan, formatRate } from '@/utils/fixtureUtils';

interface FixtureCardProps {
  fixture: Fixture;
  isSelected?: boolean;
  onToggleSelected?: (fixtureId: number) => void;
}

export function FixtureCard({ fixture, isSelected = false, onToggleSelected }: FixtureCardProps) {
  const navigate = useNavigate();

  const handleToggle = () => {
    onToggleSelected?.(fixture.fixtureId);
  };

  const handleViewDetails = () => {
    navigate(`/fixtures/${fixture.fixtureId}`, { state: fixture });
  };

  const layCan = formatLayCan(fixture.layCanStart, fixture.layCanEnd);
  const rateDetails = fixture.rate ? formatRate(fixture.rate, fixture.rateType) : null;
  const actualTceEarning = fixture.tcactInclIdleEarning
    ? `${Math.round(fixture.tcactInclIdleEarning / 1000)}K/Day`
    : 'No Data';

  const ballast = `${fixture.ballastConsumption ? fixture.ballastConsumption.toFixed(1) : '-'} mts @ ${fixture.ballastSpeed ? fixture.ballastSpeed.toFixed(1) : '-'} knts`;
  const laden = `${fixture.ladenConsumption ? fixture.ladenConsumption.toFixed(1) : '-'} mts @ ${fixture.ladenSpeed ? fixture.ladenSpeed.toFixed(1) : '-'} knts`;
  const rvTce = `${fixture.tcrtExclIdleEarning ? `${Math.trunc(fixture.tcrtExclIdleEarning / 1000)}K/Day` : '-'} @ ${fixture.tcrtInclIdleDays ? fixture.tcrtInclIdleDays.toFixed(1) : '-'} days`;
  const actualTce = `${fixture.tcactInclIdleEarning ? `${Math.trunc(fixture.tcactInclIdleEarning / 1000)}K/Day` : '-'} @ ${fixture.tcactInclIdleDays ? fixture.tcactInclIdleDays.toFixed(1) : '-'} days`;

  return (
    <div
      className={`overflow-hidden rounded-lg border border-gray-200 transition-all duration-200 dark:border-dark-700 ${
        isSelected ? 'mb-4 mt-2 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700' : ''
      }`}
      style={{ containerType: 'inline-size' }}
      id={`fixture-${fixture.fixtureId}`}
    >
      <div
        className="fixture-grid fixture-grid-chevron relative cursor-pointer bg-white px-2 py-1.5 pr-3 transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-700"
        onClick={handleToggle}
      >
        <div className="fixture-vessel flex items-center">
          <div className="mr-2 flex items-center">
            <ChevronRight
              className={`mr-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isSelected ? 'rotate-90' : ''}`}
            />
            <div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                  {fixture.vesselName}
                </span>
                <VesselBadge
                  isOwnFixture={fixture.isOwnFixture}
                  scrubberInstalled={fixture.scrubberInstalled}
                  age15Plus={fixture.age >= 15}
                />
              </div>
              {fixture.cargoSize > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {fixture.type} | {formatCargoSize(fixture.cargoSize)}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 text-right">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">
              {fixture.operator}
            </span>
            {fixture.rateExplanation && (
              <div className="mt-0.5 text-xs text-red-600 dark:text-red-400">
                {fixture.rateExplanation}
              </div>
            )}
          </div>
        </div>

        <div className="fixture-trade flex items-center">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
              {fixture.trade}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {layCan}
              {rateDetails && <> | {rateDetails}</>}
            </div>
          </div>
          <div className="text-right text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {fixture.charterer}
          </div>
        </div>

        <div className="fixture-earning flex items-center text-xs sm:text-sm">
          <FixtureStatus status={fixture.status} className="text-sm sm:text-base" />
          <span
            className={`flex-1 whitespace-nowrap text-right font-medium ${
              fixture.tcactInclIdleEarning
                ? 'text-gray-900 dark:text-white'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            {actualTceEarning}
          </span>
        </div>
      </div>

      <style>{`
        .fixture-grid {
          display: grid;
          grid-template-columns: 1fr;
        }

        .fixture-vessel,
        .fixture-trade,
        .fixture-earning {
          padding: 0.25rem;
        }

        @container (min-width: 960px) {
          .fixture-grid {
            grid-template-columns: repeat(5, 1fr);
            align-items: center;
          }

          .fixture-vessel {
            grid-column: 1 / 3;
          }

          .fixture-trade {
            grid-column: 3 / 5;
          }

          .fixture-earning {
            grid-column: 5 / 6;
          }
        }

        @media (max-width: 576px) {
          .fixture-vessel,
          .fixture-trade,
          .fixture-earning {
            padding: 0.25rem 0;
          }
        }

        .fixture-details-panel {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }

        /* Narrow card: align rows and expanded details with vessel name (grid px-2 + chevron 1.5rem) */
        @container (max-width: 959.98px) {
          .fixture-grid-chevron .fixture-trade,
          .fixture-grid-chevron .fixture-earning {
            padding-left: 1.5rem;
          }

          .fixture-details-panel {
            padding-left: 2rem;
            padding-right: 0.75rem;
          }
        }
      `}</style>

      {isSelected && (
        <div className="fixture-details-panel border-t border-gray-200 bg-gray-50 py-3 dark:border-dark-700 dark:bg-gray-900/50">
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Fixture Details
          </h4>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <DetailItem
              label="DATETIME REPORTED"
              value={formatDate(fixture.reportedTime, 'MMM d, y, h:mm a')}
            />
            <DetailItem label="BALLAST CONS/SPEED" value={ballast} />
            <DetailItem label="LADEN CONS/SPEED" value={laden} />
            <DetailItem label="IDLE DAYS" value={fixture.idleDays?.toFixed(1) || '0.0'} />
            <DetailItem label="RV TCE EXCLUDE IDLE" value={rvTce} />
            <DetailItem label="ACTUAL TCE INCLUDE IDLE" value={actualTce} />
          </div>

          <div className="mt-4 text-center">
            <Button
              variant="primary"
              size="sm"
              onClick={handleViewDetails}
              rightIcon={<ChevronRight className="h-4 w-4" />}
            >
              View Full Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DetailItemProps {
  label: string;
  value: string | null | undefined;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="border-b border-gray-200 pb-2 dark:border-gray-700 md:border-b-0 md:pb-0">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value || '--'}</div>
    </div>
  );
}
